import { Button } from 'components/ui/button';
import useAuth from 'hooks/useAuth';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MFA_CODE_LENGTH, MfaCodeInput } from 'sections/auth/mfa/MfaCodeInput';
import { classifyMfaCodeError, describeMfaCodeError, MfaCodeErrorKind } from 'sections/auth/mfa/mfaErrors';
import { cognitoUser } from 'utils/cognitoUser';
import { t } from 'utils/getTranslations';
import { logError } from 'utils/monitoring';

const SUPPORT_EMAIL = 'support@crespect.com';

/**
 * The second step of a password sign-in for an enrolled user. A wrong or expired code keeps the user
 * here: the boxes are cleared and refocused, and only the helper line changes.
 */
export function AuthMFAVerification() {
    const { sendOTP, discardCognitoSession } = useAuth();
    const navigate = useNavigate();
    // Only a live code challenge stores the sign-in email. After a refresh the holder has rebuilt a Cognito
    // user from localStorage without a challenge session, so the email is the reliable sign of one.
    const [email] = useState(() => cognitoUser.getSignInEmail());
    const [code, setCode] = useState('');
    const [errorKind, setErrorKind] = useState<MfaCodeErrorKind | null>(null);
    const [attempt, setAttempt] = useState(0);
    const [submitting, setSubmitting] = useState(false);

    // Takes the code as an argument: the completion callback fires in the same tick as the last digit's
    // change, before the state above has caught up.
    const submit = async (candidate: string) => {
        if (submitting) {
            return;
        }
        if (candidate.length !== MFA_CODE_LENGTH) {
            setErrorKind('incomplete-code');
            return;
        }
        setSubmitting(true);
        try {
            await sendOTP(candidate);
        } catch (err: unknown) {
            const kind = classifyMfaCodeError(err);
            if (kind === 'unknown') {
                logError(err);
            }
            console.error('Error verifying TOTP:', err);
            setErrorKind(kind);
            setCode('');
            setAttempt((value) => value + 1);
        } finally {
            setSubmitting(false);
        }
    };

    const backToLogin = () => {
        discardCognitoSession();
        navigate('/login', { replace: true });
    };

    useEffect(() => {
        if (!email) {
            navigate('/login', { replace: true });
        }
    }, [email, navigate]);

    if (!email) {
        return null;
    }

    const expired = errorKind === 'expired-code';
    const inlineError = errorKind && !expired ? describeMfaCodeError(errorKind) : null;

    return (
        <div className="flex flex-col">
            <h1 className="text-2xl font-semibold">{t('auth.mfa-verification-title')}</h1>
            <p
                className={expired ? 'text-destructive mt-2 mb-6 text-sm' : 'text-muted-foreground mt-2 mb-6 text-sm'}
                role={expired ? 'alert' : undefined}
            >
                {expired ? describeMfaCodeError('expired-code') : t('auth.mfa-verification-description', { email })}
            </p>
            <MfaCodeInput
                value={code}
                onChange={(value) => {
                    setCode(value);
                    setErrorKind(null);
                }}
                onComplete={(value) => {
                    void submit(value);
                }}
                error={Boolean(inlineError)}
                disabled={submitting}
                resetKey={attempt}
                ariaLabel={t('auth.mfa-code-label')}
            />
            <p
                className={inlineError ? 'text-destructive mt-2.5 text-xs' : 'text-muted-foreground mt-2.5 text-xs'}
                role={inlineError ? 'alert' : undefined}
            >
                {inlineError ?? t('auth.mfa-code-rotates')}
            </p>
            <Button
                size="lg"
                className="mt-6 w-full"
                disabled={submitting}
                onClick={() => {
                    void submit(code);
                }}
            >
                {t('continue')}
            </Button>
            <div className="mt-4 text-center">
                <Button variant="link" onClick={backToLogin}>
                    {t('auth.mfa-back-to-login')}
                </Button>
            </div>
            <p className="text-muted-foreground mt-2 text-center text-xs">
                {t('auth.mfa-lost-authenticator')}{' '}
                <a className="text-primary font-medium hover:underline" href={`mailto:${SUPPORT_EMAIL}`}>
                    {SUPPORT_EMAIL}
                </a>
            </p>
        </div>
    );
}
